// Version: 1.0.2 - Replaced console statements with logger utility
// ESLint: All imports are used - createContext (line 7), onAuthStateChanged (line 143), signOut (line 208)
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../../firebase';
import { onAuthStateChanged, signOut, signInWithCredential, GoogleAuthProvider } from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  setDoc,
  getDoc,
  Timestamp,
} from 'firebase/firestore';
import { googleAuth, handleRedirectResult } from './GoogleAuthContext';
import { createLogger } from '../../utils/logger';


const logger = createLogger('AuthContext');
const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  logger.debug('[AuthContext] AuthProvider initializing');
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(''); 
    const [userName, setUserName] = useState('');
    const [userId, setUserId] = useState('');
    const [userImage, setUserImage] = useState('');
    const [userDocId, setUserDocId] = useState('');
    const [userImageFlag, setUserImageFlag] = useState(false);
    const [employeeSupermarketId, setEmployeeSupermarketId] = useState(null);

  const persistUser = useCallback(async (name, authUid, imgUrl, email = null) => {
      // Validate that we have a proper Auth UID
      if (!authUid || authUid === 'MISSING_AUTH_UID') {
        throw new Error('Invalid Auth UID provided for user creation');
      }

    // Double-check that no user exists before creating (prevent race conditions)
    // Check both by identity_id AND by email if we have an email

    // First check by identity_id
    const identityQuery = query(collection(db, 'Users'), where('identity_id', '==', authUid));
    const identitySnapshot = await getDocs(identityQuery);

    if (!identitySnapshot.empty) {
      logger.warn(
        `[AuthContext] User with identity_id ${authUid} already exists, skipping creation`
      );
      const existingDoc = identitySnapshot.docs[0];
      const existingData = existingDoc.data();
      
      return {
        firestoreDocId: existingDoc.id,
        ...existingData,
      };
    }

    // Also check by email field if we have an email
    if (email) {
      const emailQuery = query(collection(db, 'Users'), where('email', '==', email));
      const emailSnapshot = await getDocs(emailQuery);

      if (!emailSnapshot.empty) {
        logger.warn(`[AuthContext] User with email ${email} already exists, skipping creation`);
        const existingDoc = emailSnapshot.docs[0];
        const existingData = existingDoc.data();
        logger.debug(`[AuthContext] Returning existing user ${existingDoc.id} with role: ${existingData.role_name}`);
        return {
          firestoreDocId: existingDoc.id,
          ...existingData,
        };
      }
      }
      
      // Clean and validate the data before creating the document
      const cleanName = name ? name.trim() : 'User';
      const cleanImgUrl = imgUrl ? imgUrl.trim() : '';
      
      const newUserDoc = {
        first_name: cleanName,
        identity_id: authUid, // This is the Firebase Auth UID
      role_name: 'Regular',
        created_at: Timestamp.fromDate(new Date()),
        photo: cleanImgUrl,
      email: email || (authUid.includes('@') ? authUid : null), // Store email from parameter or authUid if it's an email
      };
      
      try {
      // Use Firebase Auth UID as document ID instead of auto-generated ID
      const userDocRef = doc(db, 'Users', authUid);
      
      const existingDocCheck = await getDoc(userDocRef);
      if (existingDocCheck.exists()) {
        const existingDataBefore = existingDocCheck.data();
        
        // CRITICAL FIX: If document exists, preserve existing role and other critical fields
        // Only update fields that are missing or need to be set, don't overwrite existing role
        const fieldsToUpdate = {};
        if (!existingDataBefore.first_name && newUserDoc.first_name) {
          fieldsToUpdate.first_name = newUserDoc.first_name;
        }
        if (!existingDataBefore.identity_id || existingDataBefore.identity_id === 'MISSING_AUTH_UID') {
          fieldsToUpdate.identity_id = newUserDoc.identity_id;
        }
        if (!existingDataBefore.email && newUserDoc.email) {
          fieldsToUpdate.email = newUserDoc.email;
        }
        if (!existingDataBefore.photo && newUserDoc.photo) {
          fieldsToUpdate.photo = newUserDoc.photo;
        }
        if (!existingDataBefore.created_at) {
          fieldsToUpdate.created_at = newUserDoc.created_at;
        }
        
        // Only update if there are fields to update
        if (Object.keys(fieldsToUpdate).length > 0) {
          await updateDoc(userDocRef, fieldsToUpdate);
          return { firestoreDocId: authUid, ...existingDataBefore, ...fieldsToUpdate };
        } else {
          return { firestoreDocId: authUid, ...existingDataBefore };
        }
      }
      
      // Document doesn't exist at this path, safe to create with setDoc merge
      // However, to be extra safe, remove role_name from the object to prevent accidental overwrites
      // if a document gets created between check and write (race condition)
      const { role_name, ...newUserDocWithoutRole } = newUserDoc;
      await setDoc(userDocRef, newUserDocWithoutRole, { merge: true });
      // Then explicitly set role_name only if it doesn't already exist
      const finalDocCheck = await getDoc(userDocRef);
      if (finalDocCheck.exists()) {
        const finalDocData = finalDocCheck.data();
        if (!finalDocData.role_name) {
          await updateDoc(userDocRef, { role_name: 'Regular' });
        }
      }
      
      logger.debug(
        `[AuthContext] Created new user: ${authUid} with email: ${newUserDoc.email || 'no-email'}`
      );
      return { firestoreDocId: authUid, ...newUserDoc };
      } catch (error) { 
      logger.error('[AuthContext] Error persisting user:', error);
        throw error; // Re-throw to be caught by caller if needed
      }
    }, []);

  const getUser = useCallback(
    async (userAuth) => {
      let finalUserRole = '';
      let finalEmployeeSupermarketId = null;
      let finalUserName = '';
      let finalUserId = '';
      let finalUserImage = '';
      let finalUserDocId = '';

      try {
        // Check for users by identity_id first
        const userQuery = query(collection(db, 'Users'), where('identity_id', '==', userAuth.uid));
        let querySnapshot = await getDocs(userQuery);

        // If no user found by identity_id, also check by email if user has an email
        if (querySnapshot.empty && userAuth.email) {
          logger.debug(
            `[AuthContext] No user found by identity_id ${userAuth.uid}, checking by email ${userAuth.email}`
          );
          const emailQuery = query(collection(db, 'Users'), where('email', '==', userAuth.email));
          querySnapshot = await getDocs(emailQuery);

        if (!querySnapshot.empty) {
            logger.debug(
              `[AuthContext] Found existing user by email ${userAuth.email}, using existing account`
            );
            logger.debug(
              `[AuthContext] Note: User logged in with different auth method (UID: ${userAuth.uid}) but using existing account (identity_id: ${querySnapshot.docs[0].data().identity_id})`
            );
            // Use the existing user without modifying identity_id
            // This allows users to login with different auth methods (Google, email/password, etc.)
            // but maintain the same account and data
          }
        }

        // CRITICAL FIX: Only create new user if NOT found by identity_id AND NOT found by email
        if (querySnapshot.empty) {
          logger.debug(`[AuthContext] No existing user found, creating new user for ${userAuth.email || userAuth.uid}`);
          const persistedData = await persistUser(
            userAuth.displayName,
            userAuth.uid,
            userAuth.photoURL,
            userAuth.email
          );
          finalUserDocId = persistedData.firestoreDocId;
          finalUserId = persistedData.identity_id;
          finalUserRole = persistedData.role_name;
          finalUserName = persistedData.first_name;
          finalUserImage = persistedData.photo;
          finalEmployeeSupermarketId = null; // Default for new user
        } else {
          // Process existing user (found by identity_id OR email)
          let userDoc; // Declare outside the if/else blocks

          // Check for duplicate users and handle them
          if (querySnapshot.docs.length > 1) {
            logger.warn(
              `[AuthContext] Found ${querySnapshot.docs.length} duplicate users for ${userAuth.email || userAuth.uid}`
            );

            // Sort by role priority (Admin first) then by created_at timestamp (oldest first for admin)
            const sortedDocs = querySnapshot.docs.sort((a, b) => {
              const aData = a.data();
              const bData = b.data();
              
              // Prioritize Admin role
              if (aData.role_name === 'Admin' && bData.role_name !== 'Admin') return -1;
              if (bData.role_name === 'Admin' && aData.role_name !== 'Admin') return 1;
              
              // If same role, keep oldest (lowest created_at timestamp)
              const aTime = aData.created_at?.toMillis() || 0;
              const bTime = bData.created_at?.toMillis() || 0;
              return aTime - bTime; // Oldest first
            });

            // Keep the first (Admin or oldest), delete the rest
            userDoc = sortedDocs[0]; // Keep the admin user or oldest
            const duplicateDocs = sortedDocs.slice(1); // Delete the rest

            logger.debug(
              `[AuthContext] Keeping user ${userDoc.id} (role: ${userDoc.data().role_name}), deleting ${duplicateDocs.length} duplicates`
            );

            // Delete duplicate documents
            for (const duplicateDoc of duplicateDocs) {
              try {
                await duplicateDoc.ref.delete();
                logger.debug(`[AuthContext] Deleted duplicate user: ${duplicateDoc.id}`);
              } catch (deleteError) {
                logger.error(
                  `[AuthContext] Failed to delete duplicate user ${duplicateDoc.id}:`,
                  deleteError
                );
              }
            }
          } else {
            // Single user found (normal case)
            userDoc = querySnapshot.docs[0];
          }

          const userData = userDoc.data();
          finalUserDocId = userDoc.id;
          
          // Check if user has proper identity_id, if not, update it
          if (!userData.identity_id || userData.identity_id === 'MISSING_AUTH_UID') {
            try {
              await userDoc.ref.update({
                identity_id: userAuth.uid,
                updated_at: Timestamp.fromDate(new Date()),
              });
              finalUserId = userAuth.uid;
              logger.debug(`[AuthContext] Updated identity_id for user ${userDoc.id} to ${userAuth.uid}`);
            } catch (updateError) {
              logger.error(
                `[AuthContext] Failed to update identity_id for user ${userAuth.uid}:`,
                updateError
              );
              finalUserId = userAuth.uid; // Use Auth UID as fallback
            }
          } else {
            finalUserId = userData.identity_id;
          }
          
          finalUserRole = userData.role_name || 'Regular';
          // Support both first_name and displayName fields for backward compatibility
          finalUserName = userData.first_name || userData.displayName || 'User';
          finalUserImage = userData.photo || userAuth.photoURL || '';
          finalEmployeeSupermarketId = userData.employeeSupermarketId || null;
          if (!userData.photo && userAuth.photoURL) setUserImageFlag(true); // This can remain a direct state update for UI reactivity
          
          logger.debug(`[AuthContext] Successfully loaded existing user: ${finalUserName} (${finalUserRole})`);
        }
      } catch (error) {
        logger.error(
          '[AuthContext] Error within getUser (fetching or persisting Firestore data):',
          error
        );
        
        // Don't set fallback values here - let the caller handle retries
        throw error; // Re-throw the error to be handled by the retry mechanism
      }
      
      // Note: Admin access should be managed through Firebase custom claims or Firestore user role
      // Use Firebase Admin SDK to set custom claims for admin users
      
      // Set all states together after all async operations in getUser are done
      logger.debug(`[AuthContext] Setting user states - Role: ${finalUserRole}, Name: ${finalUserName}, DocId: ${finalUserDocId}`);
      setUserDocId(finalUserDocId);
      setUserId(finalUserId);
      setUserRole(finalUserRole);
      setUserName(finalUserName);
      setUserImage(finalUserImage);
      setEmployeeSupermarketId(finalEmployeeSupermarketId);

      // setLoading(false) will be handled by the caller (onAuthStateChanged effect)
    },
    [persistUser]
  );

    useEffect(() => {
        const updateFirestoreUserImage = async () => {
            if (userDocId && userImage && userImageFlag) {
                const userDocRef = doc(db, 'Users', userDocId);
                try {
                    await updateDoc(userDocRef, { photo: userImage });
                    setUserImageFlag(false);
                } catch (error) {
          logger.error('[AuthContext] Error updating user image in Firestore:', error);
                }
            }
        };
        if (userImageFlag && userDocId) {
            updateFirestoreUserImage();
        }
    }, [userDocId, userImage, userImageFlag]);
   
    useEffect(() => {
        setLoading(true); // Explicitly set loading true when subscription starts or getUser changes
    logger.debug('[AuthContext] Setting up auth state listener');
    let unsubscribe = null;
    let cancelled = false;
    (async () => {
      try {
        await handleRedirectResult();
      } catch (err) {
        if (err?.code !== 'auth/invalid-credential') logger.error('[AuthContext] Redirect result error', err);
      }
      if (cancelled) return; // Effect unmounted before listener was registered; skip to avoid orphaned listener
      unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;
      logger.debug(`[AuthContext] Auth state changed - user: ${user ? user.email : 'null'}, uid: ${user ? user.uid : 'null'}`);
            if (user) {
                setCurrentUser(user); // Set Firebase Auth user object

                // CRITICAL: Wait for auth token to propagate to Firestore client before any Firestore ops.
                // New users can get permission-denied if Firestore uses a stale token before propagation.
                try {
                  await user.getIdToken(true);
                } catch (tokenError) {
                  logger.error('[AuthContext] Failed to refresh auth token:', tokenError);
                }
                if (cancelled) return;

        // Retry mechanism with permission-denied handling (token propagation race condition)
        const maxRetries = 3;
                const attemptGetUser = async (attemptNumber) => {
                    try {
                        await getUser(user); // Fetch/create Firestore profile & set related states
                        return true;
                    } catch (error) {
                        const isPermissionDenied =
                          error?.code === 'permission-denied' ||
                          error?.message?.includes('permission-denied');

                        if (isPermissionDenied && attemptNumber < maxRetries) {
                          logger.warn(
                            `[AuthContext] Permission denied (token propagation?), waiting before retry (attempt ${attemptNumber}/${maxRetries})`
                          );
                          await new Promise((resolve) => setTimeout(resolve, 1000));
                        }
                        logger.error(`[AuthContext] Attempt ${attemptNumber} failed:`, error);
                        return false;
                    }
                };

        let success = false;
        for (let attempt = 1; attempt <= maxRetries && !success; attempt++) {
          success = await attemptGetUser(attempt);

          if (!success && attempt < maxRetries) {
            // Wait before retrying with exponential backoff
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Max 5 seconds
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }

        if (!success) {
          if (cancelled) return;
          logger.error('[AuthContext] All retry attempts failed, setting fallback values');
                        // Only set fallback values if we've exhausted all retries
                        setUserRole('Regular'); // Default/fallback role
                        setUserName(user.displayName || 'User (Profile Error)');
                        setUserId(user.uid);
                        setUserImage(user.photoURL || '');
                        setUserDocId('');
                        setEmployeeSupermarketId(null);
                }
            } else {
          logger.debug('[AuthContext] No user authenticated, clearing all states');
                setCurrentUser(null);
                setUserRole('');
                setUserName('');
                setUserId('');
                setUserImage('');
                setUserDocId('');
                setEmployeeSupermarketId(null);
            }
        if (cancelled) return;
        logger.debug('[AuthContext] Auth processing complete, setting loading to false');
            setLoading(false); // Set loading false AFTER all user processing is done
        });
    })();
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
    }, [getUser]);

    function signup(email, password) {
        return auth.createUserWithEmailAndPassword(email, password);
    }

    function login(email, password, type) {
    if (type === undefined || type === 'regular')
        return auth.signInWithEmailAndPassword(email, password);
      return googleAuth();
    }

  /** Sign in with Google ID token from GSI - bypasses popup/redirect, works on Safari/iOS */
  function loginWithGoogleCredential(idToken) {
    const credential = GoogleAuthProvider.credential(idToken);
    return signInWithCredential(auth, credential);
  }

  function logout() {
      return signOut(auth);
    }

    function resetPassword(email) {
      return auth.sendPasswordResetEmail(email);
    }

  const updateUserName = (newName) => {
    setUserName(newName);
  };

    const value = {
        currentUser,
        userRole,
        userName,
        userId,
        userImage,
        userDocId, // Export userDocId as it's used for image updates
        employeeSupermarketId,
        loading,
        login,
        loginWithGoogleCredential,
        logout,
        signup,
    resetPassword,
    updateUserName,
    };

  return (
    <AuthContext.Provider value={value}>
      {/* Children are rendered regardless of loading state here; consumers check useAuth().loading */}
      {/* However, the outer AuthProvider still has !loading && children in the original code snippet provided for original question... let's assume the consumer uses the loading flag from context. */}
      {/* For clarity, the original pattern was: `{!loading && children}` directly in this return. If that was intended, keep it.*/}
      {/* If consumers check useAuth().loading, then just `{children}` is fine here. */}
      {children} 
    </AuthContext.Provider>
  );
}
